import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Loader2, Check, Pencil, Save, Trash2, CheckCircle2, XCircle, HelpCircle, Zap, Upload, X, FileText, Image as ImageIcon, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { motion } from "framer-motion";

interface Question {
  question_text: string;
  question_type: "multiple_choice" | "true_false" | "essay";
  difficulty: "easy" | "medium" | "hard";
  options?: string[];
  correct_answer: string;
  explanation?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lessonId: string;
  lessonTitle: string;
  lessonContent: string;
  nurseryId: string;
  classId?: string | null;
  onSuccess: () => void;
}

const difficultyColors: Record<string, string> = {
  easy: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  hard: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const difficultyLabels: Record<string, string> = { easy: "سهل", medium: "متوسط", hard: "صعب" };
const typeLabels: Record<string, string> = { multiple_choice: "اختيار من متعدد", true_false: "صح وخطأ", essay: "مقالي" };
const typeIcons: Record<string, React.ReactNode> = {
  multiple_choice: <CheckCircle2 className="w-3.5 h-3.5" />,
  true_false: <XCircle className="w-3.5 h-3.5" />,
  essay: <HelpCircle className="w-3.5 h-3.5" />,
};

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB per file
const MAX_FILES = 10;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function GenerateQuestionsDialog({ open, onOpenChange, lessonId, lessonTitle, lessonContent, nurseryId, classId, onSuccess }: Props) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  // Multi-file upload state
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [contentSource, setContentSource] = useState<"text" | "file">(lessonContent?.trim() ? "text" : "file");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Settings
  const [questionCount, setQuestionCount] = useState("5");
  const [difficulty, setDifficulty] = useState("mixed");
  const [selectedTypes, setSelectedTypes] = useState<string[]>(["multiple_choice", "true_false"]);
  const [examTitle, setExamTitle] = useState(`اختبار: ${lessonTitle}`);
  const [duration, setDuration] = useState("15");

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const validTypes = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    const validFiles: File[] = [];

    for (const file of files) {
      if (uploadedFiles.length + validFiles.length >= MAX_FILES) {
        toast({ title: "تنبيه", description: `الحد الأقصى ${MAX_FILES} ملفات`, variant: "destructive" });
        break;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast({ title: "خطأ", description: `${file.name} أكبر من 15MB`, variant: "destructive" });
        continue;
      }
      if (!validTypes.includes(file.type)) {
        toast({ title: "خطأ", description: `${file.name} نوع غير مدعوم. يُقبل PDF أو صور فقط`, variant: "destructive" });
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...validFiles]);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (contentSource !== "file") setContentSource("file");
    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;

    const validTypes = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    const validFiles: File[] = [];

    for (const file of files) {
      if (uploadedFiles.length + validFiles.length >= MAX_FILES) {
        toast({ title: "تنبيه", description: `الحد الأقصى ${MAX_FILES} ملفات`, variant: "destructive" });
        break;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast({ title: "خطأ", description: `${file.name} أكبر من 15MB`, variant: "destructive" });
        continue;
      }
      if (!validTypes.includes(file.type)) {
        toast({ title: "خطأ", description: `${file.name} نوع غير مدعوم`, variant: "destructive" });
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...validFiles]);
    }
  }, [contentSource, uploadedFiles.length, toast]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const generateQuestions = async () => {
    const hasText = contentSource === "text" && lessonContent?.trim();
    const hasFiles = contentSource === "file" && uploadedFiles.length > 0;

    if (!hasText && !hasFiles) {
      toast({ title: "خطأ", description: contentSource === "file" ? "يرجى رفع ملف واحد على الأقل" : "لا يوجد محتوى في الدرس لتوليد أسئلة منه", variant: "destructive" });
      return;
    }
    if (selectedTypes.length === 0) {
      toast({ title: "خطأ", description: "اختر نوع سؤال واحد على الأقل", variant: "destructive" });
      return;
    }

    setGenerating(true);
    try {
      const body: Record<string, any> = {
        lessonTitle,
        questionCount: parseInt(questionCount),
        difficulty,
        questionTypes: selectedTypes,
      };

      if (contentSource === "text") {
        body.lessonContent = lessonContent;
      } else if (uploadedFiles.length > 0) {
        // Convert all files to base64
        const filesData = await Promise.all(
          uploadedFiles.map(async (file) => ({
            base64: await fileToBase64(file),
            mimeType: file.type,
            name: file.name,
            size: file.size,
          }))
        );
        body.files = filesData;
        // Keep backward compat for single file
        if (filesData.length === 1) {
          body.fileBase64 = filesData[0].base64;
          body.fileMimeType = filesData[0].mimeType;
          body.fileName = filesData[0].name;
        }
      }

      const { data, error } = await supabase.functions.invoke("generate-questions", { body });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setQuestions(data.questions || []);
      toast({ title: `✨ تم توليد ${data.questions?.length || 0} أسئلة بنجاح` });
    } catch (e: any) {
      console.error("Generate error:", e);
      toast({ title: "خطأ", description: e.message || "فشل في توليد الأسئلة", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const saveQuestionsOnly = async () => {
    if (questions.length === 0) return;
    setSaving(true);
    try {
      const rows = questions.map((q) => ({
        nursery_id: nurseryId,
        lesson_id: lessonId,
        question_text: q.question_text,
        question_type: q.question_type,
        difficulty: q.difficulty,
        options: q.options || null,
        correct_answer: q.correct_answer,
        explanation: q.explanation || null,
        is_ai_generated: true,
      }));

      const { error } = await supabase.from("questions").insert(rows);
      if (error) throw error;

      toast({ title: `✅ تم حفظ ${questions.length} سؤال في بنك الأسئلة` });
      setQuestions([]);
      onSuccess();
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const saveAndCreateExam = async () => {
    if (questions.length === 0) return;
    setSaving(true);
    try {
      const rows = questions.map((q) => ({
        nursery_id: nurseryId,
        lesson_id: lessonId,
        question_text: q.question_text,
        question_type: q.question_type,
        difficulty: q.difficulty,
        options: q.options || null,
        correct_answer: q.correct_answer,
        explanation: q.explanation || null,
        is_ai_generated: true,
      }));

      const { data: savedQuestions, error: qError } = await supabase.from("questions").insert(rows).select("id");
      if (qError) throw qError;

      const { data: exam, error: examError } = await supabase.from("exams").insert({
        title: examTitle.trim() || `اختبار: ${lessonTitle}`,
        nursery_id: nurseryId,
        class_id: classId || null,
        lesson_id: lessonId,
        duration_minutes: parseInt(duration) || 15,
        max_score: savedQuestions.length,
        is_interactive: true,
      }).select("id").single();

      if (examError) throw examError;

      const examQuestions = savedQuestions.map((q, i) => ({
        exam_id: exam.id,
        question_id: q.id,
        sort_order: i,
        points: 1,
      }));

      const { error: linkError } = await supabase.from("exam_questions").insert(examQuestions);
      if (linkError) throw linkError;

      toast({ title: `🎉 تم إنشاء امتحان "${examTitle}" مع ${savedQuestions.length} سؤال` });
      setQuestions([]);
      onSuccess();
      onOpenChange(false);
      navigate(`/dashboard/exam/${exam.id}/take`);
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const updateQuestion = (index: number, updated: Partial<Question>) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...updated } : q)));
  };

  const totalFileSize = uploadedFiles.reduce((sum, f) => sum + f.size, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            <span>توليد أسئلة وامتحان من الدرس</span>
          </DialogTitle>
        </DialogHeader>

        {questions.length === 0 ? (
          <ScrollArea className="flex-1 max-h-[70vh]">
            <div className="space-y-4 pl-2">
              {/* Hero section */}
              <div className="bg-gradient-to-l from-primary/10 to-primary/5 rounded-xl p-5 text-center space-y-2">
                <div className="text-4xl">🤖✨</div>
                <p className="font-bold text-foreground">توليد أسئلة بالذكاء الاصطناعي</p>
                <p className="text-sm text-muted-foreground">سيقوم الذكاء الاصطناعي بتحليل درس "{lessonTitle}" وتوليد أسئلة مناسبة</p>
              </div>

              {/* Content Source Tabs */}
              <div>
                <Label className="mb-2 block">مصدر المحتوى</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={contentSource === "text" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setContentSource("text")}
                    className="flex-1 rounded-xl"
                  >
                    <FileText className="w-4 h-4 ml-1" />
                    نص الدرس
                  </Button>
                  <Button
                    type="button"
                    variant={contentSource === "file" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setContentSource("file")}
                    className="flex-1 rounded-xl"
                  >
                    <Upload className="w-4 h-4 ml-1" />
                    رفع ملفات PDF أو صور
                  </Button>
                </div>
              </div>

              {/* Multi-file upload area */}
              {contentSource === "file" && (
                <div className="space-y-3">
                  {/* Uploaded files list */}
                  {uploadedFiles.length > 0 && (
                    <div className="space-y-2">
                      {uploadedFiles.map((file, index) => {
                        const isImage = file.type.startsWith("image/");
                        return (
                          <motion.div
                            key={`${file.name}-${index}`}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-3 bg-muted/50 rounded-xl p-3 border border-border"
                          >
                            {isImage ? (
                              <div className="w-12 h-12 rounded-lg overflow-hidden border bg-muted shrink-0">
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <FileText className="w-10 h-10 text-red-500 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{file.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {(file.size / (1024 * 1024)).toFixed(1)} MB
                                {isImage ? " • صورة" : " • PDF"}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 shrink-0 text-destructive hover:text-destructive"
                              onClick={() => removeFile(index)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </motion.div>
                        );
                      })}

                      {/* Summary */}
                      <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                        <span>{uploadedFiles.length} / {MAX_FILES} ملفات</span>
                        <span>{(totalFileSize / (1024 * 1024)).toFixed(1)} MB إجمالي</span>
                      </div>
                    </div>
                  )}

                  {/* Upload zone */}
                  {uploadedFiles.length < MAX_FILES && (
                    <div
                      className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                        isDragging
                          ? "border-primary bg-primary/10 scale-[1.02]"
                          : "border-muted-foreground/25 hover:border-primary/50"
                      }`}
                      onClick={() => fileInputRef.current?.click()}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                    >
                      {isDragging ? (
                        <>
                          <Upload className="w-10 h-10 mx-auto text-primary mb-2 animate-bounce" />
                          <p className="text-sm font-medium text-primary">أفلت الملفات هنا</p>
                        </>
                      ) : uploadedFiles.length === 0 ? (
                        <>
                          <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                          <p className="text-sm font-medium text-muted-foreground">اسحب وأفلت الملفات هنا أو اضغط للرفع</p>
                          <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG — حتى 15MB لكل ملف — حتى {MAX_FILES} ملفات</p>
                        </>
                      ) : (
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                          <Plus className="w-5 h-5" />
                          <span>اسحب أو اضغط لإضافة ملفات أخرى</span>
                        </div>
                      )}
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    className="hidden"
                    onChange={handleFileSelect}
                    multiple
                  />
                </div>
              )}

              {contentSource === "text" && !lessonContent?.trim() && (
                <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-xl p-3 text-center">
                  <p className="text-sm text-yellow-700 dark:text-yellow-400">⚠️ لا يوجد محتوى نصي لهذا الدرس. جرب رفع ملفات PDF أو صور بدلاً.</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>عدد الأسئلة</Label>
                  <Select value={questionCount} onValueChange={setQuestionCount}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["3", "5", "7", "10", "15", "20"].map((n) => (
                        <SelectItem key={n} value={n}>{n} أسئلة</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>مستوى الصعوبة</Label>
                  <Select value={difficulty} onValueChange={setDifficulty}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mixed">متنوع</SelectItem>
                      <SelectItem value="easy">سهل</SelectItem>
                      <SelectItem value="medium">متوسط</SelectItem>
                      <SelectItem value="hard">صعب</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">أنواع الأسئلة</Label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { value: "multiple_choice", label: "اختيار من متعدد" },
                    { value: "true_false", label: "صح وخطأ" },
                    { value: "essay", label: "مقالي" },
                  ].map((t) => (
                    <label key={t.value} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedTypes.includes(t.value)}
                        onCheckedChange={() => toggleType(t.value)}
                      />
                      <span className="text-sm">{t.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <Button
                onClick={generateQuestions}
                disabled={generating}
                className="w-full h-12 text-base rounded-xl bg-gradient-to-l from-primary to-primary/80 shadow-lg"
              >
                {generating ? (
                  <><Loader2 className="w-5 h-5 animate-spin ml-2" />جاري التوليد بالذكاء الاصطناعي...</>
                ) : (
                  <><Sparkles className="w-5 h-5 ml-2" />🚀 توليد الأسئلة {contentSource === "file" && uploadedFiles.length > 0 ? `(${uploadedFiles.length} ملف)` : ""}</>
                )}
              </Button>
            </div>
          </ScrollArea>
        ) : (
          <div className="flex flex-col flex-1 min-h-0 space-y-3">
            {/* Exam settings */}
            <div className="bg-gradient-to-l from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10 rounded-xl p-4 border border-green-200 dark:border-green-800 space-y-3">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-green-600" />
                <span className="font-bold text-sm text-green-700 dark:text-green-400">إنشاء امتحان تلقائي</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">عنوان الامتحان</Label>
                  <Input
                    value={examTitle}
                    onChange={(e) => setExamTitle(e.target.value)}
                    className="h-9 text-sm"
                    placeholder="عنوان الامتحان"
                  />
                </div>
                <div>
                  <Label className="text-xs">المدة (دقائق)</Label>
                  <Select value={duration} onValueChange={setDuration}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["5", "10", "15", "20", "30", "45"].map((d) => (
                        <SelectItem key={d} value={d}>{d} دقيقة</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                ✏️ {questions.length} سؤال — راجع وعدّل:
              </p>
              <div className="flex gap-1">
                {questions.map((q, i) => (
                  <Badge key={i} variant="outline" className={`text-[10px] ${difficultyColors[q.difficulty]}`}>
                    {difficultyLabels[q.difficulty]}
                  </Badge>
                ))}
              </div>
            </div>

            <ScrollArea className="flex-1 max-h-[35vh]">
              <div className="space-y-2 pl-2">
                {questions.map((q, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="border rounded-xl p-3 space-y-1.5 bg-card"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-bold text-muted-foreground bg-muted rounded-full w-6 h-6 flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0 space-y-1.5">
                        {editIndex === idx ? (
                          <div className="space-y-2">
                            <Textarea
                              value={q.question_text}
                              onChange={(e) => updateQuestion(idx, { question_text: e.target.value })}
                              className="text-sm min-h-[60px]"
                            />
                            {q.options && (
                              <div className="space-y-1">
                                {q.options.map((opt, oi) => (
                                  <Input
                                    key={oi}
                                    value={opt}
                                    onChange={(e) => {
                                      const newOptions = [...q.options!];
                                      newOptions[oi] = e.target.value;
                                      updateQuestion(idx, { options: newOptions });
                                    }}
                                    className="h-8 text-sm"
                                  />
                                ))}
                              </div>
                            )}
                            <Input
                              value={q.correct_answer}
                              onChange={(e) => updateQuestion(idx, { correct_answer: e.target.value })}
                              className="h-8 text-sm"
                              placeholder="الإجابة الصحيحة"
                            />
                            <Button size="sm" onClick={() => setEditIndex(null)} className="gap-1">
                              <Save className="w-3 h-3" /> حفظ
                            </Button>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-medium">{q.question_text}</p>
                            {q.options && (
                              <div className="grid grid-cols-2 gap-1">
                                {q.options.map((opt, oi) => (
                                  <div
                                    key={oi}
                                    className={`text-xs px-2 py-1 rounded-lg border ${
                                      opt === q.correct_answer ? "bg-green-50 border-green-300 text-green-800 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700" : "bg-muted/30"
                                    }`}
                                  >
                                    {opt === q.correct_answer && "✅ "}{opt}
                                  </div>
                                ))}
                              </div>
                            )}
                            {q.question_type === "true_false" && (
                              <p className="text-xs text-green-600 dark:text-green-400">✅ {q.correct_answer}</p>
                            )}
                            {q.question_type === "essay" && (
                              <p className="text-xs text-muted-foreground">💡 {q.correct_answer}</p>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Badge variant="outline" className="text-[10px] gap-0.5">
                          {typeIcons[q.question_type]}
                          {typeLabels[q.question_type]}
                        </Badge>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditIndex(editIndex === idx ? null : idx)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeQuestion(idx)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </ScrollArea>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={saveQuestionsOnly}
                disabled={saving || questions.length === 0}
                className="flex-1"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : <Check className="w-4 h-4 ml-1" />}
                حفظ في البنك فقط
              </Button>
              <Button
                onClick={saveAndCreateExam}
                disabled={saving || questions.length === 0 || !examTitle.trim()}
                className="flex-1"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : <Zap className="w-4 h-4 ml-1" />}
                حفظ + إنشاء امتحان
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
